import { zodResolver } from "@hookform/resolvers/zod";
import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router";
import { z } from "zod";

import { AuthShell } from "../components/auth/AuthShell.js";
import { ErrorState, LoadingState } from "../components/data/index.js";
import { Button } from "../components/ui/button.js";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../components/ui/form.js";
import { Input } from "../components/ui/input.js";
import { useSignupResendMutation, useSignupVerifyMutation } from "../lib/api/auth.js";
import {
  authDestination,
  clearAuthReturnTo,
  invitationTokenFromReturnTo,
  readAuthReturnTo,
  safeAuthReturnTo,
} from "../lib/auth/return-to.js";
import { APIError } from "../lib/http.js";

const resendSchema = z.object({ email: z.string().email().max(255) });

// "pending" → verifying; "redirecting" → fresh verify, auto-logged-in, leaving
// for the invitation/company destination; "already" → token was already consumed (idempotent re-click /
// StrictMode double-fire), so there's no session — offer manual sign in;
// "error" → genuine failure (bad/expired token, missing token).
type Outcome = "pending" | "redirecting" | "already" | "error";

export function VerifyEmailPage(): JSX.Element {
  const [params] = useSearchParams();
  const token = params.get("token");
  const returnTo = safeAuthReturnTo(params.get("returnTo"));
  return <VerifyEmail key={token} token={token} returnTo={returnTo} />;
}

function VerifyEmail({
  token,
  returnTo,
}: {
  token: string | null;
  returnTo: string | null;
}): JSX.Element {
  const navigate = useNavigate();
  const resendMut = useSignupResendMutation();
  const verifyMut = useSignupVerifyMutation();
  const firedRef = useRef(false);
  const [showResend, setShowResend] = useState(false);
  const [outcome, setOutcome] = useState<Outcome>(token ? "pending" : "error");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const resendForm = useForm<{ email: string }>({
    resolver: zodResolver(resendSchema),
    defaultValues: { email: "" },
  });

  useEffect(() => {
    if (!token || firedRef.current) return;
    firedRef.current = true;
    verifyMut
      .mutateAsync({ token })
      .then((data) => {
        const destination = authDestination(
          returnTo ?? readAuthReturnTo(data.user.email),
          data.user.tenantCode,
        );
        clearAuthReturnTo();
        setOutcome("redirecting");
        window.location.replace(destination);
      })
      .catch((e: unknown) => {
        // A token already consumed (re-click / StrictMode double-fire) means the
        // email IS verified — but there's no session, so route to sign in rather
        // than show a scary error. An EXPIRED token is also 410 but is a genuine
        // failure (code TOKEN_EXPIRED) → fall through to the error+resend path.
        const status = e instanceof APIError ? e.status : 0;
        const code = e instanceof APIError ? e.problem.code : undefined;
        if ((status === 410 || status === 409) && code === "TOKEN_ALREADY_USED") {
          setOutcome("already");
          return;
        }
        setErrorMessage(e instanceof APIError ? e.displayDetail : "Verification failed");
        setOutcome("error");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot on mount
  }, [token]);

  const goToSignIn = (): void => {
    const r = returnTo ?? readAuthReturnTo();
    void navigate(r ? `/login?returnTo=${encodeURIComponent(r)}` : "/login");
  };

  return (
    <AuthShell
      title="Verify your email"
      subtitle={
        outcome === "error"
          ? "We couldn’t verify this link."
          : "Confirming your email and signing you in."
      }
    >
      {outcome === "pending" ? (
        <LoadingState message="Verifying your email…" />
      ) : outcome === "redirecting" ? (
        <LoadingState message="Email verified — continuing…" />
      ) : outcome === "already" ? (
        <div className="flex flex-col gap-4">
          <p className="rounded-md border border-border bg-muted/50 p-3 text-center text-sm text-foreground">
            Your email is already verified. Please sign in to continue.
          </p>
          <Button size="lg" className="w-full rounded-full" onClick={goToSignIn}>
            Go to sign in
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <ErrorState
            title="Verification failed"
            description={
              errorMessage ??
              (!token
                ? "This verification link is missing its token. Request a new one below."
                : undefined)
            }
          />
          {!showResend ? (
            <Button
              variant="outline"
              className="w-full rounded-full"
              onClick={() => setShowResend(true)}
            >
              Resend verification email
            </Button>
          ) : (
            <Form {...resendForm}>
              <form
                onSubmit={resendForm.handleSubmit((v) =>
                  resendMut.mutate({
                    ...v,
                    invitationToken: invitationTokenFromReturnTo(
                      returnTo ?? readAuthReturnTo(v.email),
                    ),
                  }),
                )}
                className="flex flex-col gap-3"
                noValidate
              >
                <FormField
                  control={resendForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Your email</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="Enter Email ID" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {resendMut.isSuccess ? (
                  <p className="text-sm text-muted-foreground">
                    If your email is registered and unverified, we sent a new link.
                    {resendMut.data.devLink ? (
                      <>
                        {" "}
                        <a href={resendMut.data.devLink} className="text-primary hover:underline">
                          Dev: open new verify link
                        </a>
                      </>
                    ) : null}
                  </p>
                ) : resendMut.error instanceof APIError ? (
                  <p className="text-sm text-destructive">{resendMut.error.displayDetail}</p>
                ) : null}
                <Button
                  type="submit"
                  className="w-full rounded-full"
                  disabled={resendMut.isPending}
                >
                  {resendMut.isPending ? "Sending…" : "Send verification email"}
                </Button>
              </form>
            </Form>
          )}
          <Button variant="ghost" onClick={goToSignIn}>
            Back to sign in
          </Button>
        </div>
      )}
    </AuthShell>
  );
}
